import { z } from "zod";

const envSchema = z
  .object({
    VITE_APP_TYPE: z.string().default("web"),
    VITE_APP_ID: z.string().default("default"),
    VITE_APP_ID2: z.string().default("default"),
    VITE_APP_NAME: z.string().default("Switch"),
    VITE_API_URL: z.string().url().default("https://localhost:3001/api"),
    VITE_WEBSOCKET_URL: z.string().url().default("wss://localhost:3002/ws"),
    VITE_SPLASH_SCREEN: z.string().url().default("https://placehold.co/400x400"),
    VITE_SPLASH_BG: z.string().default("#1a1a2e"),
    VITE_SENTRY_DSN: z.string().url().default("https://placeholder@sentry.io/0"),
    VITE_TURN_SERVER: z.string().default("turn:localhost:3478"),
    VITE_GIPHY_API_KEY: z.string().default("placeholder"),
    VITE_PUBLIC_VAPID_KEY: z.string().default("placeholder"),
    VITE_CUSTOM_COLOR_THEME: z.string().optional(),
    VITE_BRAND_PRIMARY_COLOR: z.string().default("#007AFF"),
  })
  .passthrough();

const env = envSchema.parse(import.meta.env);

const runtimeTenantConfigSchema = z.object({
  appType: z.string().default("web"),
  appId: z.string().min(1),
  appId2: z.string().min(1).optional(),
  appName: z.string().min(1),
  apiUrl: z.string().url(),
  websocketUrl: z.string().url(),
  splashScreen: z.string().url(),
  splashScreenBg: z.string(),
  sentryDsn: z.string().url(),
  turnServer: z.string(),
  giphyApiKey: z.string(),
  publicVapidKey: z.string(),
  customColorTheme: z.string().optional(),
  brandPrimaryColor: z.string().optional(),
  jitsiMeetUrl: z.string().url().optional(),
  stunServers: z.array(z.string()).optional(),
});

const runtimeConfigByHostSchema = z.record(z.string().min(1), runtimeTenantConfigSchema);

export type RuntimeTenantConfig = z.infer<typeof runtimeTenantConfigSchema>;

function isLocalRuntimeHost(hostname: string): boolean {
  if (!hostname) return true;
  if (hostname === "localhost") return true;
  if (hostname.endsWith(".localhost")) return true;
  if (hostname === "127.0.0.1" || hostname === "0.0.0.0") return true;
  return /^10\./.test(hostname) || /^192\.168\./.test(hostname) || /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname);
}

interface ClientConfig {
  jitsiUrl: string | null;
  stunServers: string[];
}

export const appConfig = {
  appType: env.VITE_APP_TYPE,
  appId: env.VITE_APP_ID,
  appId2: env.VITE_APP_ID2,
  appName: env.VITE_APP_NAME,
  apiUrl: env.VITE_API_URL,
  websocketUrl: env.VITE_WEBSOCKET_URL,
  splashScreen: env.VITE_SPLASH_SCREEN,
  splashScreenBg: env.VITE_SPLASH_BG,
  sentryDsn: env.VITE_SENTRY_DSN,
  turnServer: env.VITE_TURN_SERVER,
  jitsiMeetUrl: null as string | null,
  giphyApiKey: env.VITE_GIPHY_API_KEY,
  stunServers: [] as string[],
  publicVapidKey: env.VITE_PUBLIC_VAPID_KEY,
  customColorTheme: env.VITE_CUSTOM_COLOR_THEME,
  brandPrimaryColor: env.VITE_BRAND_PRIMARY_COLOR,
};

/**
 * Apply the tenant brand color to the CSS `--primary`/`--ring` tokens so that
 * buttons, links, and accents pick it up. Defaults to #007AFF (the mobile
 * brand blue) when a tenant does not override `brandPrimaryColor`.
 *
 * @platform-only: web view-layer theming — writes CSS custom properties on the
 * DOM. Each platform applies brand color through its own native channel (CSS
 * vars here, SwiftUI Color on iOS, colors.xml on Android); there is no shared
 * runtime behavior to host in core. The hex→HSL triple matches what shadcn's
 * tokens expect (Tailwind wraps them in `hsl(var(--primary))`).
 */
export function applyBrandColor(hex: string | undefined): void {
  const hsl = hexToHslTriple(hex || "#007AFF");
  if (!hsl) return;
  const root = document.documentElement;
  root.style.setProperty("--primary", hsl);
  root.style.setProperty("--ring", hsl);
}

function hexToHslTriple(hex: string): string | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const int = parseInt(m[1], 16);
  const r = ((int >> 16) & 255) / 255;
  const g = ((int >> 8) & 255) / 255;
  const b = (int & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function applyRuntimeTenantConfig(config: RuntimeTenantConfig): void {
  appConfig.appType = config.appType || "web";
  appConfig.appId = config.appId;
  appConfig.appId2 = config.appId2 || config.appId;
  appConfig.appName = config.appName;
  appConfig.apiUrl = config.apiUrl;
  appConfig.websocketUrl = config.websocketUrl;
  appConfig.splashScreen = config.splashScreen;
  appConfig.splashScreenBg = config.splashScreenBg;
  appConfig.sentryDsn = config.sentryDsn;
  appConfig.turnServer = config.turnServer;
  appConfig.giphyApiKey = config.giphyApiKey;
  appConfig.publicVapidKey = config.publicVapidKey;
  appConfig.customColorTheme = config.customColorTheme;
  if (config.brandPrimaryColor) {
    appConfig.brandPrimaryColor = config.brandPrimaryColor;
  }
  if (config.jitsiMeetUrl) {
    appConfig.jitsiMeetUrl = config.jitsiMeetUrl;
  }
  if (config.stunServers?.length) {
    appConfig.stunServers = config.stunServers;
  }
}

/**
 * Resolve tenant config for hosted environments from hostname.
 * Local development continues to use generated .env values.
 */
export async function loadRuntimeTenantConfig(): Promise<void> {
  if (typeof window === "undefined") return;

  const hostname = window.location.hostname.toLowerCase();
  if (isLocalRuntimeHost(hostname)) {
    // Local dev uses the generated .env value already in appConfig.
    applyBrandColor(appConfig.brandPrimaryColor);
    return;
  }

  const response = await fetch("/runtime-config/by-host.json", {
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error(`[app-config] runtime config fetch failed: ${response.status}`);
  }

  const payload = runtimeConfigByHostSchema.parse(await response.json());
  const direct = payload[hostname];
  const noWww = hostname.startsWith("www.") ? payload[hostname.slice(4)] : undefined;
  const config = direct || noWww;

  if (!config) {
    throw new Error(`[app-config] unknown hostname: ${hostname}`);
  }

  applyRuntimeTenantConfig(config);
  applyBrandColor(appConfig.brandPrimaryColor);
  console.log(`[app-config] runtime tenant config loaded for ${hostname}: ${appConfig.appId}`);
}

/**
 * Fetch client config from server and update appConfig.
 * Should be called during app initialization.
 */
export async function loadClientConfig(): Promise<void> {
  try {
    const response = await fetch(`${appConfig.apiUrl}/client-config`);
    if (!response.ok) {
      console.warn("[app-config] Failed to fetch client config:", response.status);
      return;
    }
    const config: ClientConfig = await response.json();
    if (config.jitsiUrl) {
      appConfig.jitsiMeetUrl = config.jitsiUrl;
    }
    if (config.stunServers?.length) {
      appConfig.stunServers = config.stunServers;
    }
    console.log("[app-config] Loaded client config from server:", config);
  } catch (err) {
    console.warn("[app-config] Error fetching client config:", err);
  }
}
